import {
  COUNTRY_OPTIONS,
  EMPTY_PERCENT_ROW,
  INITIAL_ACCEPT_SUPPLIER_FORM,
} from "./acceptSupplierInvitationConstants";

export function sumPercentages(rows) {
  return rows.reduce((sum, row) => sum + (Number(row.percentage) || 0), 0);
}

export function toNonNegativeInt(value) {
  const n = parseInt(String(value), 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

export function mapPercentRows(rows) {
  return rows
    .filter((r) => r.name?.trim())
    .map((r) => ({
      name: r.name.trim(),
      percentage: Number(r.percentage) || 0,
    }));
}

export function mapCounts(block) {
  return {
    male: toNonNegativeInt(block.male),
    female: toNonNegativeInt(block.female),
    others: toNonNegativeInt(block.others),
  };
}

export function formatApiErrorMessage(result) {
  if (!result) return "Request failed. Please try again.";
  const raw = result.message ?? result.data?.message ?? result.data?.error;
  if (Array.isArray(raw)) return raw.join("\n");
  if (typeof raw === "string" && raw.trim()) return raw;
  return "Failed to accept invitation. Please check your entries and try again.";
}

export function countryLabelForApi(countryCode) {
  return (
    COUNTRY_OPTIONS.find((c) => c.value === countryCode)?.label ??
    countryCode ??
    ""
  );
}

export function countryCodeFromLabel(countryLabel) {
  if (!countryLabel) return "IN";
  const normalized = String(countryLabel).trim().toLowerCase();
  const match = COUNTRY_OPTIONS.find(
    (c) =>
      c.label.toLowerCase() === normalized ||
      c.value.toLowerCase() === normalized
  );
  return match?.value ?? "IN";
}

export function mapPercentRowsFromApi(rows) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [EMPTY_PERCENT_ROW()];
  }
  return rows.map((r) => ({
    name: r?.name ?? "",
    percentage:
      r?.percentage !== undefined && r?.percentage !== null
        ? String(r.percentage)
        : "",
  }));
}

export function mapCountBlockFromApi(block) {
  const src = block || {};
  const toStr = (v) =>
    v !== undefined && v !== null && v !== "" ? String(v) : "";
  return {
    male: toStr(src.male),
    female: toStr(src.female),
    others: toStr(src.others),
  };
}

export function parseContactName(contactName) {
  const parts = (contactName || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { first_name: "", last_name: "" };
  return {
    first_name: parts[0],
    last_name: parts.slice(1).join(" "),
  };
}

/** API may return booleans as true, 1, or "true". */
export function invitationFlagTrue(value) {
  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    String(value).toLowerCase() === "true"
  );
}

export function resolveInvitationCompanyId(inv, supplierWrapper) {
  const raw =
    supplierWrapper?.company_id ??
    inv?.company_id ??
    inv?.invited_by ??
    inv?.buyer_company_id ??
    null;
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 1 ? n : null;
}

/** Maps GET suppliers/invitation/:token response into form state. */
export function parseInvitationResponse(inv) {
  const canUpdate = invitationFlagTrue(inv?.can_update);
  const contactOnlyUpdate = invitationFlagTrue(inv?.contact_only_update);
  const supplierWrapper = inv?.supplier;
  const supplier = supplierWrapper?.supplier ?? null;
  const profile = supplier?.profile ?? null;
  const contact = parseContactName(inv?.invited_contact_name);

  const industryId = supplier?.industry_id ?? profile?.industry_id ?? null;
  const subIndustryId =
    supplier?.sub_industry_id ?? profile?.sub_industry_id ?? null;

  const hasExistingSupplier = Boolean(supplier && profile);
  const companyId = resolveInvitationCompanyId(inv, supplierWrapper);
  const supplierId = supplier?.id ?? inv?.supplier_id ?? null;

  if (!supplier && !profile) {
    return {
      canUpdate,
      hasExistingSupplier: false,
      contactOnlyUpdate,
      companyId: companyId ?? null,
      supplierId: null,
      industryId: "",
      subIndustryId: "",
      form: {
        ...INITIAL_ACCEPT_SUPPLIER_FORM,
        first_name: contact.first_name,
        last_name: contact.last_name,
        email: inv?.invited_email ?? "",
        mobile: inv?.invited_phone ?? "",
        company_name: inv?.invited_company_name ?? "",
      },
    };
  }

  const contactFromInvitation = contactOnlyUpdate;

  return {
    canUpdate,
    hasExistingSupplier,
    contactOnlyUpdate,
    companyId,
    supplierId,
    industryId: industryId != null ? String(industryId) : "",
    subIndustryId: subIndustryId != null ? String(subIndustryId) : "",
    form: {
      ...INITIAL_ACCEPT_SUPPLIER_FORM,
      first_name: contactFromInvitation
        ? contact.first_name
        : supplier?.first_name ?? contact.first_name,
      last_name: contactFromInvitation
        ? contact.last_name
        : supplier?.last_name ?? contact.last_name,
      email: contactFromInvitation
        ? inv?.invited_email ?? ""
        : supplier?.email ?? inv?.invited_email ?? "",
      mobile: contactFromInvitation
        ? inv?.invited_phone ?? ""
        : supplier?.mobile_number ?? inv?.invited_phone ?? "",
      country: countryCodeFromLabel(profile?.country),
      entity_type: profile?.entity_type ?? "PRIVATE_LIMITED",
      cin: profile?.cin ?? "",
      gstin: profile?.gstin ?? "",
      pan: profile?.pan ?? "",
      company_name: supplier?.company_name ?? inv?.invited_company_name ?? "",
      address1: profile?.address1 ?? "",
      address2: profile?.address2 ?? "",
      city: profile?.city ?? "",
      state: profile?.state ?? "",
      zip_code: profile?.zip_code ?? "",
      employee_size_category: profile?.employee_size_category ?? "51-100",
      business_activities: mapPercentRowsFromApi(profile?.business_activities),
      products_or_services: mapPercentRowsFromApi(profile?.products_or_services),
      manufacturing_plants: {
        national:
          profile?.manufacturing_plants?.national != null
            ? String(profile.manufacturing_plants.national)
            : "",
        international:
          profile?.manufacturing_plants?.international != null
            ? String(profile.manufacturing_plants.international)
            : "",
      },
      offices: {
        national:
          profile?.offices?.national != null
            ? String(profile.offices.national)
            : "",
        international:
          profile?.offices?.international != null
            ? String(profile.offices.international)
            : "",
      },
      permanent_employees: mapCountBlockFromApi(profile?.permanent_employees),
      non_permanent_employees: mapCountBlockFromApi(
        profile?.non_permanent_employees
      ),
      password: "",
      confirm_password: "",
    },
  };
}

export function buildRegisteredAddressLine(form) {
  return [
    form.address1?.trim(),
    form.address2?.trim(),
    form.city?.trim(),
    form.state?.trim(),
    form.zip_code?.trim(),
  ]
    .filter(Boolean)
    .join(", ");
}

export function buildAcceptPayload(form, industryId, subIndustryId) {
  const payload = {
    first_name: form.first_name?.trim() || "",
    last_name: form.last_name?.trim() || "",
    email: form.email?.trim() || "",
    mobile: form.mobile?.trim() || "",
    country: countryLabelForApi(form.country),
    entity_type: form.entity_type || "",
    company_name: form.company_name?.trim() || "",
    address1: form.address1?.trim() || "",
    address2: form.address2?.trim() || "",
    city: form.city?.trim() || "",
    state: form.state?.trim() || "",
    zip_code: form.zip_code?.trim() || "",
    registered_address: buildRegisteredAddressLine(form),
    industry: parseInt(String(industryId), 10),
    sub_industry: parseInt(String(subIndustryId), 10),
    employee_size_category: form.employee_size_category || "",
    business_activities: mapPercentRows(form.business_activities),
    products_or_services: mapPercentRows(form.products_or_services),
    manufacturing_plants: {
      national: toNonNegativeInt(form.manufacturing_plants?.national),
      international: toNonNegativeInt(form.manufacturing_plants?.international),
    },
    offices: {
      national: toNonNegativeInt(form.offices?.national),
      international: toNonNegativeInt(form.offices?.international),
    },
    permanent_employees: mapCounts(form.permanent_employees),
    non_permanent_employees: mapCounts(form.non_permanent_employees),
  };

  const password = form.password?.trim();
  if (password) {
    payload.password = password;
  }

  if (
    form.entity_type === "LIMITED" ||
    form.entity_type === "PRIVATE_LIMITED"
  ) {
    payload.cin = form.cin?.trim() || "";
  } else if (form.entity_type === "PROPRIETORSHIP") {
    payload.gstin = form.gstin?.trim() || "";
  } else if (form.entity_type === "OTHER") {
    payload.pan = form.pan?.trim() || "";
  }

  return payload;
}
